import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  useTheme,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Divider,
} from '@mui/material';
import { Remove as RemoveIcon, Add as AddIcon, Search as SearchIcon, TrendingUp as TrendingUpIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { playerService, Player, User, FantasyTeam, FantasyTeamPlayer } from '../services/playerService';
import { nblService } from '../services/nblService';
import { PlayerCurrentDayStats } from '../types';
import { useUser } from '../contexts/UserContext';
import { usePlayer } from '../contexts/PlayerContext';
import GamePlayerDialog from '../components/GamePlayerDialog';

const MyTeamPage: React.FC = () => {
  const theme = useTheme();
  const { currentUsername, isUserSwitching } = useUser();
  const { openPlayerDialog } = usePlayer();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userFantasyTeams, setUserFantasyTeams] = useState<FantasyTeam[]>([]);
  const [userPlayers, setUserPlayers] = useState<Player[]>([]);
  const [teamPlayers, setTeamPlayers] = useState<FantasyTeamPlayer[]>([]);
  const [currentDayStats, setCurrentDayStats] = useState<PlayerCurrentDayStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dropPlayerDialog, setDropPlayerDialog] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [gamePlayerDialog, setGamePlayerDialog] = useState<{
    open: boolean;
    player: Player | null;
    gameId: number | null;
  }>({ open: false, player: null, gameId: null });
  const [playerGameIdMap, setPlayerGameIdMap] = useState<Map<number, number>>(new Map());

  // Memoize the loadMyTeamData function to prevent recreation on every render
  const loadMyTeamData = useCallback(async () => {
    if (!currentUsername) return;
    
    try {
      setLoading(true);
      setError(null);

      console.log(`🔄 MyTeamPage: Loading data for user "${currentUsername}"...`);
      
      // Load user by username
      const user = await playerService.getUserByUsername(currentUsername);
      console.log('✅ MyTeamPage: User loaded:', user);
      setCurrentUser(user);

      // Load user's fantasy teams
      const fantasyTeams = await playerService.getFantasyTeamsByUser(user.id);
      console.log('✅ MyTeamPage: Fantasy teams loaded:', fantasyTeams);
      setUserFantasyTeams(fantasyTeams);

      // Load all players for potential additions
      const allPlayers = await playerService.getAllPlayers();
      console.log('✅ MyTeamPage: All players loaded:', allPlayers.length);

      // Get the actual players on the user's fantasy team from the database
      if (fantasyTeams.length > 0) {
        const primaryTeam = fantasyTeams[0];
        // Load team players with lineup positions
        const teamPlayersData = await playerService.getTeamPlayers(primaryTeam.id);
        setTeamPlayers(teamPlayersData);
        
        const players = teamPlayersData.map(ftp => ftp.player);
        setUserPlayers(players);
        console.log('✅ MyTeamPage: Team players loaded from database:', teamPlayersData.length);

        // Load current day fantasy points for these players
        if (players.length > 0) {
          const playerIds = players.map(p => p.id);
          const today = new Date();
          const todayStats = await nblService.getCurrentDayPlayerStats(playerIds);
          setCurrentDayStats(todayStats);
          console.log('✅ MyTeamPage: Current day stats loaded:', todayStats.length);

          // Get games for today to map players to game IDs
          const todayGames = await nblService.getGamesByDate(today);
          const gameIdMap = new Map<number, number>();
          
          // For each game, get player stats to map players to game IDs
          for (const game of todayGames) {
            try {
              const gamePlayerStats = await nblService.getPlayerGamesByGameId(game.id);
              gamePlayerStats.forEach(playerStat => {
                if (playerIds.includes(playerStat.playerId)) {
                  gameIdMap.set(playerStat.playerId, game.id);
                }
              });
            } catch (err) {
              console.warn(`Failed to load player stats for game ${game.id}:`, err);
            }
          }
          
          setPlayerGameIdMap(gameIdMap);
        }
      } else {
        setUserPlayers([]);
        setTeamPlayers([]);
        setCurrentDayStats([]);
        console.log('⚠️ MyTeamPage: No fantasy teams found for user');
      }

    } catch (err) {
      console.error('❌ MyTeamPage: Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [currentUsername]);

  // Load data when component mounts or when user changes
  useEffect(() => {
    if (currentUsername && !isUserSwitching) {
      loadMyTeamData();
    }
  }, [currentUsername, isUserSwitching, loadMyTeamData]);

  // Listen for player add/remove events to refresh data
  useEffect(() => {
    const handlePlayerAdded = () => {
      loadMyTeamData();
    };
    const handlePlayerRemoved = () => {
      loadMyTeamData();
    };

    window.addEventListener('playerAdded', handlePlayerAdded);
    window.addEventListener('playerRemoved', handlePlayerRemoved);

    return () => {
      window.removeEventListener('playerAdded', handlePlayerAdded);
      window.removeEventListener('playerRemoved', handlePlayerRemoved);
    };
  }, [loadMyTeamData]);

  // Load current day stats when user players change
  useEffect(() => {
    if (userPlayers.length > 0) {
      refreshCurrentDayStats();
    }
  }, [userPlayers]);

  // Get the primary team (first team)
  const primaryTeam = userFantasyTeams[0];

  const refreshCurrentDayStats = async () => {
    if (userPlayers.length > 0) {
      try {
        const playerIds = userPlayers.map(p => p.id);
        const today = new Date();
        const todayStats = await nblService.getCurrentDayPlayerStats(playerIds);
        setCurrentDayStats(todayStats);
        console.log('✅ MyTeamPage: Current day stats refreshed:', todayStats.length);

        // Update game ID map
        const todayGames = await nblService.getGamesByDate(today);
        const gameIdMap = new Map<number, number>();
        
        for (const game of todayGames) {
          try {
            const gamePlayerStats = await nblService.getPlayerGamesByGameId(game.id);
            gamePlayerStats.forEach(playerStat => {
              if (playerIds.includes(playerStat.playerId)) {
                gameIdMap.set(playerStat.playerId, game.id);
              }
            });
          } catch (err) {
            console.warn(`Failed to load player stats for game ${game.id}:`, err);
          }
        }
        
        setPlayerGameIdMap(gameIdMap);
      } catch (error) {
        console.error('Error refreshing current day stats:', error);
      }
    }
  };

  // Debug logging
  console.log('MyTeamPage render:', {
    currentUser,
    userFantasyTeams: userFantasyTeams?.length || 0,
    userPlayers: userPlayers?.length || 0,
    loading,
    error
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  // Show loading state while user is switching
  if (isUserSwitching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Switching to {currentUsername}...
        </Typography>
      </Box>
    );
  }

  // Safety check for primaryTeam
  if (!primaryTeam) {
    console.log('No fantasy team found:', userFantasyTeams);
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
        <Alert severity="info">
          No fantasy team found. Please create a team first.
        </Alert>
      </Box>
    );
  }

  // Safety check for userPlayers
  if (!userPlayers || userPlayers.length === 0) {
    console.log('No players found in userPlayers:', userPlayers);
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
        <Alert severity="info">
          No players found on your team. Please add players from the Players page first.
        </Alert>
      </Box>
    );
  }

  // Helper function to get player at a specific lineup position
  const getPlayerAtPosition = (position: string): FantasyTeamPlayer | null => {
    return teamPlayers.find(tp => tp.lineupPosition === position) || null;
  };

  // Get inactive players (no lineup position or INACTIVE)
  const inactivePlayers = teamPlayers.filter(tp => !tp.lineupPosition || tp.lineupPosition === 'INACTIVE');

  // Helper function to check if a position is already taken by another player
  const isPositionTaken = (position: string, excludePlayerId?: number): boolean => {
    return teamPlayers.some(
      tp => tp.lineupPosition === position && 
            tp.playerId !== excludePlayerId &&
            tp.lineupPosition !== 'INACTIVE'
    );
  };

  // Handle updating player lineup position
  const handleUpdateLineupPosition = async (playerId: number, newPosition: string | null) => {
    if (!primaryTeam) return;

    // Check if the position is already taken (unless it's INACTIVE or null)
    if (newPosition && newPosition !== 'INACTIVE' && isPositionTaken(newPosition, playerId)) {
      const existingPlayer = teamPlayers.find(tp => tp.lineupPosition === newPosition && tp.playerId !== playerId);
      if (existingPlayer) {
        setSnackbarMessage(`❌ Position ${newPosition} is already taken by ${existingPlayer.player.name}`);
        setSnackbarOpen(true);
        return;
      }
    }

    try {
      // Update optimistically in local state first
      setTeamPlayers(prevTeamPlayers => {
        const updatedTeamPlayers = [...prevTeamPlayers];
        
        // Find the player being moved
        const playerIndex = updatedTeamPlayers.findIndex(tp => tp.playerId === playerId);
        if (playerIndex === -1) return prevTeamPlayers;
        
        // Update the moved player's position
        updatedTeamPlayers[playerIndex] = {
          ...updatedTeamPlayers[playerIndex],
          lineupPosition: newPosition
        };
        
        return updatedTeamPlayers;
      });
      
      // Make the API call
      await playerService.updatePlayerLineupPosition(primaryTeam.id, playerId, newPosition);
      
      setSnackbarMessage('✅ Lineup position updated successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error updating lineup position:', error);
      // Revert on error by reloading data
      await loadMyTeamData();
      setSnackbarMessage(`❌ Failed to update lineup position: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setSnackbarOpen(true);
    }
  };

  // Lineup positions
  const startingPositions = ['PG', 'SG', 'SF', 'PF', 'C'];
  const benchPositions = ['BENCH1', 'BENCH2', 'BENCH3'];

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getPositionColor = (position: string) => {
    const colors: Record<string, string> = {
      'PG': theme.palette.primary.main,
      'SG': theme.palette.secondary.main,
      'SF': theme.palette.warning.main,
      'PF': theme.palette.info.main,
      'C': theme.palette.success.main,
    };
    return colors[position] || theme.palette.grey[500];
  };

  // Helper function to get today's score for a player
  const getTodayScore = (playerId: number) => {
    const stat = currentDayStats.find(s => s.playerId === playerId);
    return stat ? stat.fantasyPoints : undefined;
  };

  const handleDropPlayer = (player: Player) => {
    setSelectedPlayer(player);
    setDropPlayerDialog(true);
  };


  const confirmDropPlayer = async () => {
    if (!selectedPlayer || !primaryTeam) {
      setSnackbarMessage('No player or team selected.');
      setSnackbarOpen(true);
      return;
    }

    try {
      console.log(`🔄 Dropping ${selectedPlayer.name} from ${primaryTeam.name}...`);
      
      // Call the API to remove the player
      await playerService.removePlayerFromTeam(primaryTeam.id, selectedPlayer.id);
      
      // Update the local state immediately for responsive UI
      setUserFantasyTeams(prevTeams => 
        prevTeams.map(team => 
          team.id === primaryTeam.id 
            ? { 
                ...team, 
                players: (team.players || []).filter(p => 
                  p.playerId !== selectedPlayer.id
                )
              }
            : team
        )
      );
      
      // Update the user players list
      setUserPlayers(prev => prev.filter(p => p.id !== selectedPlayer.id));
      
      // Update team players list
      setTeamPlayers(prev => prev.filter(tp => tp.playerId !== selectedPlayer.id));
      
      // Close dialog and reset
      setDropPlayerDialog(false);
      setSelectedPlayer(null);
      
      setSnackbarMessage(`✅ ${selectedPlayer.name} dropped from ${primaryTeam.name}!`);
      setSnackbarOpen(true);
      
      console.log('✅ Player dropped successfully from team');
      
    } catch (error) {
      console.error('❌ Error dropping player from team:', error);
      setSnackbarMessage(`❌ Failed to drop ${selectedPlayer.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setSnackbarOpen(true);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      {/* Team Header */}
      {primaryTeam && (
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center' }}>
              🏀 {primaryTeam.name}
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Box sx={{ flex: '1 1 30%', textAlign: 'center' }}>
                <Typography color="text.secondary">Owner:</Typography>
                <Typography sx={{ fontWeight: 'bold' }}>{currentUser?.firstName} {currentUser?.lastName}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 30%', textAlign: 'center' }}>
                <Typography color="text.secondary">Players:</Typography>
                <Typography sx={{ fontWeight: 'bold' }}>{userPlayers.length}/12</Typography>
              </Box>
              <Box sx={{ flex: '1 1 30%', textAlign: 'center' }}>
                <Typography color="text.secondary">Status:</Typography>
                <Typography sx={{ fontWeight: 'bold' }}>Active</Typography>
              </Box>
            </Box>
            
            {/* Add Player Info */}
            {userPlayers.length >= 12 && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Roster is full (12/12 players)
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Vertical Lineup Layout */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            🏀 Starting Lineup & Bench
          </Typography>
          
          {/* Combined Starting Positions and Bench - Vertical List */}
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '80px' }}>Position</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Player</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: '100px' }}>Today's Score</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', width: '200px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Starting Positions */}
                {startingPositions.map((pos) => {
                  const teamPlayer = getPlayerAtPosition(pos);
                  const player = teamPlayer?.player;
                  const todayScore = player ? getTodayScore(player.id) : undefined;
                  const gameId = player ? playerGameIdMap.get(player.id) : undefined;
                  
                  return (
                    <TableRow key={pos} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell>
                        <Chip label={pos} sx={{ bgcolor: getPositionColor(pos), color: 'white', fontWeight: 'bold' }} />
                      </TableCell>
                      <TableCell>
                        {player ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: getPositionColor(pos), width: 40, height: 40 }}>
                              {getInitials(player.name)}
                            </Avatar>
                            <Box sx={{ cursor: 'pointer' }} onClick={() => openPlayerDialog(player)}>
                              <Typography variant="body1" sx={{ fontWeight: 'bold', '&:hover': { textDecoration: 'underline' } }}>
                                {player.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {player.team} • {player.position}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No player assigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {player && todayScore !== undefined ? (
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 'bold',
                              color: 'primary.main',
                              cursor: gameId ? 'pointer' : 'default',
                              '&:hover': gameId ? { textDecoration: 'underline' } : {},
                            }}
                            onClick={() => {
                              if (gameId && player) {
                                setGamePlayerDialog({
                                  open: true,
                                  player: player,
                                  gameId: gameId,
                                });
                              }
                            }}
                          >
                            {todayScore.toFixed(1)}
                          </Typography>
                        ) : player ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {player ? (
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={pos}
                              onChange={(e) => handleUpdateLineupPosition(player.id, e.target.value === 'INACTIVE' ? null : e.target.value)}
                            >
                              <MenuItem value={pos}>Keep {pos}</MenuItem>
                              {benchPositions.map(bp => (
                                <MenuItem 
                                  key={bp} 
                                  value={bp}
                                  disabled={isPositionTaken(bp, player.id)}
                                >
                                  Move to {bp} {isPositionTaken(bp, player.id) ? '(taken)' : ''}
                                </MenuItem>
                              ))}
                              {startingPositions.filter(sp => sp !== pos).map(sp => (
                                <MenuItem 
                                  key={sp} 
                                  value={sp}
                                  disabled={isPositionTaken(sp, player.id)}
                                >
                                  Move to {sp} {isPositionTaken(sp, player.id) ? '(taken)' : ''}
                                </MenuItem>
                              ))}
                              <MenuItem value="INACTIVE">Move to Inactive</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value=""
                              displayEmpty
                              onChange={(e) => {
                                const selectedPlayerId = parseInt(e.target.value);
                                if (selectedPlayerId) {
                                  handleUpdateLineupPosition(selectedPlayerId, pos);
                                }
                              }}
                            >
                              <MenuItem value="" disabled>
                                <em>Select Player</em>
                              </MenuItem>
                              {inactivePlayers.map(tp => (
                                <MenuItem 
                                  key={tp.playerId} 
                                  value={tp.playerId}
                                  disabled={isPositionTaken(pos)}
                                >
                                  {tp.player.name} {isPositionTaken(pos) ? '(position taken)' : ''}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                
                {/* Bench Positions - No separate header */}
                {benchPositions.map((pos) => {
                  const teamPlayer = getPlayerAtPosition(pos);
                  const player = teamPlayer?.player;
                  const todayScore = player ? getTodayScore(player.id) : undefined;
                  const gameId = player ? playerGameIdMap.get(player.id) : undefined;
                  
                  return (
                    <TableRow key={pos} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell>
                        <Chip label={pos} sx={{ bgcolor: theme.palette.grey[500], color: 'white', fontWeight: 'bold' }} />
                      </TableCell>
                      <TableCell>
                        {player ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: theme.palette.grey[500], width: 40, height: 40 }}>
                              {getInitials(player.name)}
                            </Avatar>
                            <Box sx={{ cursor: 'pointer' }} onClick={() => openPlayerDialog(player)}>
                              <Typography variant="body1" sx={{ fontWeight: 'bold', '&:hover': { textDecoration: 'underline' } }}>
                                {player.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {player.team} • {player.position}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No player assigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {player && todayScore !== undefined ? (
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 'bold',
                              color: 'primary.main',
                              cursor: gameId ? 'pointer' : 'default',
                              '&:hover': gameId ? { textDecoration: 'underline' } : {},
                            }}
                            onClick={() => {
                              if (gameId && player) {
                                setGamePlayerDialog({
                                  open: true,
                                  player: player,
                                  gameId: gameId,
                                });
                              }
                            }}
                          >
                            {todayScore.toFixed(1)}
                          </Typography>
                        ) : player ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {player ? (
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={pos}
                              onChange={(e) => handleUpdateLineupPosition(player.id, e.target.value === 'INACTIVE' ? null : e.target.value)}
                            >
                              {startingPositions.filter(sp => sp !== pos).map(sp => (
                                <MenuItem 
                                  key={sp} 
                                  value={sp}
                                  disabled={isPositionTaken(sp, player.id)}
                                >
                                  Move to {sp} {isPositionTaken(sp, player.id) ? '(taken)' : ''}
                                </MenuItem>
                              ))}
                              <MenuItem value={pos}>Keep {pos}</MenuItem>
                              {benchPositions.map(bp => (
                                <MenuItem 
                                  key={bp} 
                                  value={bp}
                                  disabled={isPositionTaken(bp, player.id)}
                                >
                                  Move to {bp} {isPositionTaken(bp, player.id) ? '(taken)' : ''}
                                </MenuItem>
                              ))}
                              <MenuItem value="INACTIVE">Move to Inactive</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value=""
                              displayEmpty
                              onChange={(e) => {
                                const selectedPlayerId = parseInt(e.target.value);
                                if (selectedPlayerId) {
                                  handleUpdateLineupPosition(selectedPlayerId, pos);
                                }
                              }}
                            >
                              <MenuItem value="" disabled>
                                <em>Select Player</em>
                              </MenuItem>
                              {inactivePlayers.map(tp => (
                                <MenuItem 
                                  key={tp.playerId} 
                                  value={tp.playerId}
                                  disabled={isPositionTaken(pos)}
                                >
                                  {tp.player.name} {isPositionTaken(pos) ? '(position taken)' : ''}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                
                {/* Total Score Row */}
                <TableRow sx={{ bgcolor: 'action.selected', '& td': { borderTop: '2px solid', borderColor: 'divider' } }}>
                  <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      Total Score Today:
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {(() => {
                        const allActivePlayers = [...startingPositions, ...benchPositions]
                          .map(pos => getPlayerAtPosition(pos))
                          .filter(tp => tp !== null)
                          .map(tp => tp!.player);
                        const totalScore = allActivePlayers.reduce((sum, player) => {
                          const score = getTodayScore(player.id);
                          return sum + (score !== undefined ? score : 0);
                        }, 0);
                        return totalScore.toFixed(1);
                      })()}
                    </Typography>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            ⏸️ Inactive ({inactivePlayers.length})
          </Typography>
          
          {inactivePlayers.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Player</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', width: '100px' }}>Today's Score</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', width: '200px' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inactivePlayers.map((teamPlayer) => {
                    const player = teamPlayer.player;
                    const todayScore = getTodayScore(player.id);
                    const gameId = playerGameIdMap.get(player.id);
                    
                    return (
                      <TableRow key={teamPlayer.playerId} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: theme.palette.grey[400], width: 40, height: 40 }}>
                              {getInitials(player.name)}
                            </Avatar>
                            <Box sx={{ cursor: 'pointer' }} onClick={() => openPlayerDialog(player)}>
                              <Typography variant="body1" sx={{ fontWeight: 'bold', '&:hover': { textDecoration: 'underline' } }}>
                                {player.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {player.team} • {player.position}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {todayScore !== undefined ? (
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 'bold',
                                color: 'text.secondary',
                                cursor: gameId ? 'pointer' : 'default',
                                '&:hover': gameId ? { textDecoration: 'underline' } : {},
                              }}
                              onClick={() => {
                                if (gameId) {
                                  setGamePlayerDialog({
                                    open: true,
                                    player: player,
                                    gameId: gameId,
                                  });
                                }
                              }}
                            >
                              {todayScore.toFixed(1)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value=""
                              displayEmpty
                              onChange={(e) => {
                                const newPosition = e.target.value;
                                if (newPosition) {
                                  handleUpdateLineupPosition(player.id, newPosition === 'INACTIVE' ? null : newPosition);
                                }
                              }}
                            >
                              <MenuItem value="" disabled>
                                <em>Assign Position</em>
                              </MenuItem>
                              {startingPositions.map(sp => (
                                <MenuItem 
                                  key={sp} 
                                  value={sp}
                                  disabled={isPositionTaken(sp, player.id)}
                                >
                                  Move to {sp} {isPositionTaken(sp, player.id) ? '(taken)' : ''}
                                </MenuItem>
                              ))}
                              {benchPositions.map(bp => (
                                <MenuItem 
                                  key={bp} 
                                  value={bp}
                                  disabled={isPositionTaken(bp, player.id)}
                                >
                                  Move to {bp} {isPositionTaken(bp, player.id) ? '(taken)' : ''}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No inactive players
            </Typography>
          )}
        </CardContent>
      </Card>


      {/* Drop Player Confirmation Dialog */}
      <Dialog open={dropPlayerDialog} onClose={() => setDropPlayerDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Drop Player from Roster
        </DialogTitle>
        <DialogContent>
          {selectedPlayer && (
            <Box sx={{ py: 2 }}>
              <Typography variant="h6" gutterBottom>
                Are you sure you want to drop {selectedPlayer.name}?
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {selectedPlayer.team} • {selectedPlayer.position} • #{selectedPlayer.jerseyNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedPlayer.fantasyPointsPerGame} Fantasy Points Per Game
              </Typography>
              
              <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="body2" color="warning.dark">
                  ⚠️ Warning: This action cannot be undone. The player will be removed from your roster and returned to the available player pool.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDropPlayerDialog(false)}>Cancel</Button>
          <Button 
            onClick={confirmDropPlayer} 
            variant="contained" 
            color="error"
            startIcon={<RemoveIcon />}
            disabled={!selectedPlayer}
          >
            Drop Player
          </Button>
        </DialogActions>
      </Dialog>


      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      {/* Game-Specific Player Dialog */}
      <GamePlayerDialog
        open={gamePlayerDialog.open}
        player={gamePlayerDialog.player}
        gameId={gamePlayerDialog.gameId}
        onClose={() => setGamePlayerDialog({ open: false, player: null, gameId: null })}
      />
    </Box>
  );
};

export default MyTeamPage;
